import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Button,
  EmptyList,
  ImageIcon,
  Modal,
  OverlayTooltip,
  Switch,
} from "@saleshandy/designs";

import { IProps } from "./types";
import ClientListTable from "./components/client-list-table";
import { ClientWithRole } from "../../types/client-with-role";
import { Images } from "../../../../shared/utils/app-constants";
import { AgencyRole } from "../../../../shared/enum";
import {
  hasUserPermissionToMakeAdmin,
  getAssignClientListEmptyMessage,
} from "./util/helper";

const AssignClientModal: React.FC<IProps> = ({
  show,
  onClose,
  isSelfView,
  onSubmit,
  clients,
  clientsCount,
  agencyUserName,
  isAgencyAdmin,
  isRequestPending,
  onPageChange,
  role,
}) => {
  const [initialSelectedClients, setInitialSelectedClients] = useState<
    Map<number, AgencyRole>
  >(new Map());
  const [currentSelectedClients, setCurrentSelectedClients] = useState<
    Map<number, AgencyRole>
  >(new Map());
  const [isAdminAccessChecked, setIsAdminAccessChecked] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const seenBaselineIdsRef = useRef<Set<number>>(new Set());

  const onPageChangeHandler = (page) => {
    setCurrentPage(page);
    onPageChange(page);
  };

  const handleSwitchChange = (checked: boolean) => {
    setIsAdminAccessChecked(checked ? 1 : 0);
  };

  const handleClientsToBeAssigned = (id, checked, clientRole) => {
    setCurrentSelectedClients((prev) => {
      const next = new Map(prev);
      if (checked) {
        next.set(id, clientRole);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleClientsToBeRemove = (id, checked, clientRole) => {
    if (checked) {
      handleClientsToBeAssigned(id, checked, clientRole);
      return;
    }
    setCurrentSelectedClients((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };

  const formattedClients = useMemo(
    () =>
      (clients ?? []).map((client) => {
        const selected = currentSelectedClients.has(client.clientId);
        return {
          ...client,
          isAssigned: selected,
          assignedRole: selected
            ? currentSelectedClients.get(client.clientId)
            : null,
        };
      }),
    [clients, currentSelectedClients]
  );

  const { clientsToBeAssigned, clientsToBeRemove } = useMemo(() => {
    const added: ClientWithRole[] = [];
    currentSelectedClients.forEach((roleValue, id) => {
      const initialRole = initialSelectedClients.get(id);
      if (initialRole === undefined || initialRole !== roleValue) {
        added.push({ id, role: roleValue });
      }
    });
    const removed: number[] = [];
    initialSelectedClients.forEach((_roleValue, id) => {
      if (!currentSelectedClients.has(id)) {
        removed.push(id);
      }
    });
    return { clientsToBeAssigned: added, clientsToBeRemove: removed };
  }, [initialSelectedClients, currentSelectedClients]);

  const isRoleAssigntToAllEnabledClient = () =>
    clientsToBeAssigned.some((obj) => obj.role === null);

  const hasDelta =
    clientsToBeAssigned.length > 0 || clientsToBeRemove.length > 0;
  const isAdminFlagDirty =
    (isAgencyAdmin ? 1 : 0) !== isAdminAccessChecked;
  const isSaveBtnDisable = !(hasDelta || isAdminFlagDirty);

  useEffect(() => {
    if (show && isAgencyAdmin) {
      setIsAdminAccessChecked(1);
    }
    if (!show) {
      setIsAdminAccessChecked(0);
      setInitialSelectedClients(new Map());
      setCurrentSelectedClients(new Map());
      seenBaselineIdsRef.current = new Set();
    }
  }, [show, isAgencyAdmin]);

  useEffect(() => {
    if (!show || !clients?.length) return;

    const newlyDiscovered: Array<{ id: number; role: AgencyRole }> = [];
    clients.forEach((c) => {
      if (c.isAssigned && !seenBaselineIdsRef.current.has(c.clientId)) {
        seenBaselineIdsRef.current.add(c.clientId);
        newlyDiscovered.push({ id: c.clientId, role: c.assignedRole });
      }
    });

    if (newlyDiscovered.length === 0) return;

    setInitialSelectedClients((prev) => {
      const next = new Map(prev);
      newlyDiscovered.forEach(({ id, role: roleValue }) =>
        next.set(id, roleValue)
      );
      return next;
    });

    setCurrentSelectedClients((prev) => {
      const next = new Map(prev);
      newlyDiscovered.forEach(({ id, role: roleValue }) =>
        next.set(id, roleValue)
      );
      return next;
    });
  }, [show, clients]);

  return (
    <Modal
      show={show}
      titleContent={
        <div className="d-flex justify-content-between align-items-center">
          <div className="header-txt">
            Assign clients to
            <span className="ml-1">{agencyUserName}</span>{" "}
          </div>
          {!isSelfView && hasUserPermissionToMakeAdmin(role) && (
            <div className="d-flex justify-content-between align-items-center">
              <OverlayTooltip text="Turning it ON will give access of all clients.">
                <ImageIcon src={Images.InfoCircle} className="mr-2 mt-1" />
              </OverlayTooltip>
              <Switch
                checked={isAdminAccessChecked === 1}
                variant={Switch.Variant.Default}
                size={Switch.Size.Small}
                onChange={(checked) => handleSwitchChange(checked)}
                label="Admin Access"
                className="switch admin-access-switch"
              />
            </div>
          )}
        </div>
      }
      onClose={onClose}
      onHide={onClose}
      onSubmit={() =>
        onSubmit(clientsToBeAssigned, clientsToBeRemove, isAdminAccessChecked)
      }
      submitButtonText="Save"
      hideSubmitButton={isSelfView}
      isSubmitLoading={isRequestPending}
      isSubmitDisabled={
        isSaveBtnDisable ||
        isRequestPending ||
        clients?.length === 0 ||
        isRoleAssigntToAllEnabledClient()
      }
      isCancelDisabled={isRequestPending}
      cancelButtonVarient={Button.Variant.Outlined}
      footerContent={
        <a
          href={process.env.REACT_APP_NEED_HELP_URL}
          rel="noreferrer"
          target="_blank"
          className="regular-2 link-txt"
        >
          Need Help?
        </a>
      }
      submitButtonClassName="btn-primary-disable"
      className="assign-client-modal"
    >
      {clients?.length > 0 ? (
        <ClientListTable
          clients={formattedClients}
          clientsCount={clientsCount}
          handleRoleChange={(id, checked, clientRole) =>
            handleClientsToBeAssigned(id, checked, clientRole)
          }
          handleStatusChange={(id, checked, clientRole) =>
            handleClientsToBeRemove(id, checked, clientRole)
          }
          isAdminAccess={isAdminAccessChecked === 1}
          isSelfView={isSelfView}
          onPageChange={(page) => onPageChangeHandler(page)}
          currentPage={currentPage}
        />
      ) : (
        <EmptyList
          title={getAssignClientListEmptyMessage(role)}
          description=""
          className="no-data"
          isVertical
        />
      )}
    </Modal>
  );
};

export default AssignClientModal;
